import { mapGetters, mapActions, mapMutations } from 'vuex'

export default {
  name: 'MindmapSales',
  data () {
    return {
      isLoading: {
        data: false
      },
      contentplanName: '',
      funnels: ['top', 'middle', 'bottom'],
      chipColors: [
        { id: 'existing', name: 'Published page', color: 'green lighten-4', tooltip: 'This article has been published on your website' },
        { id: 'in_progress', name: 'In progress', color: 'blue lighten-4', tooltip: 'The article has been written but not published yet.' },
        { id: 'new', name: 'New idea', color: 'gray lighten-4', tooltip: 'This is a new idea. Nothing has been written yet.' }
      ],
      rowMinHeights: { top: '100px', middle: '100px', bottom: '100px', product: '100px' },
      columnWidths: {}
    }
  },
  computed: {
    ...mapGetters([
      'topics',
      'articles'
    ]),
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    },
    sharingKey () {
      return this.$route.params.sharingKey
    },
    hasUntopicedArticles () {
      return this.articles?.some(a => a.show_in_map && (a.topic_id === null || a.topic_id === undefined))
    },
    displayTopics () {
      const currentTopics = Array.isArray(this.topics) ? [...this.topics] : []
      if (this.hasUntopicedArticles) {
        currentTopics.push({ id: 'no-hub', name: 'No Hub', isTemporary: true })
      }
      return currentTopics
    }
  },
  watch: {
    async $route () {
      await this.loadData()
    }
  },
  methods: {
    ...mapActions([
      'mindmapShareGet'
    ]),
    ...mapMutations([
      'setArticles',
      'loadTopics'
    ]),
    async loadData () {
      try {
        this.isLoading.data = true
        const response = await this.mindmapShareGet({
          contentplanId: this.contentplanId,
          key: this.sharingKey
        })
        this.contentplanName = response?.contentplan?.name || ''
        this.setArticles(response?.articles || [])
        this.loadTopics(response?.topics || [])
        this.calculateRowMinHeights()
        this.calculateColumnWidths()
        document.title = this.contentplanName ? `${this.contentplanName} | ContentGecko` : 'ContentGecko'
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading.data = false
      }
    },
    getTopicArticles (topic, funnel) {
      if (!this.articles?.length) return []
      const topicId = topic?.id

      if (topicId === 'no-hub') {
        return this.articles.filter(a =>
          a.funnel === funnel &&
          (a.topic_id === null || a.topic_id === undefined) &&
          a.show_in_map &&
          a.type !== 'product' && a.type !== 'category'
        )
      }
      return this.articles.filter(a => a.funnel === funnel && a.topic_id === topicId && a.show_in_map && a.type !== 'product' && a.type !== 'category')
    },
    getTopicProductArticles (topic) {
      if (!this.articles?.length) return []
      const topicId = topic?.id

      if (topicId === 'no-hub') {
        return this.articles.filter(a =>
          (a.type === 'product' || a.type === 'category') &&
          (a.topic_id === null || a.topic_id === undefined) &&
          a.show_in_map
        )
      }
      return this.articles.filter(a => (a.type === 'product' || a.type === 'category') && a.topic_id === topicId && a.show_in_map)
    },
    getChipColor (article) {
      if (article.article_url || article.status === 'published') return this.chipColors.find(c => c.id === 'existing').color
      else if (article.status !== 'no status') return this.chipColors.find(c => c.id === 'in_progress').color
      else return this.chipColors.find(c => c.id === 'new').color
    },
    calculateRowMinHeights () {
      const rowHeights = {}
      const allRows = [...this.funnels, 'product']
      const cellPadding = 16
      const chipHeightEstimate = 33
      const baselineMinHeight = 50

      allRows.forEach(rowKey => {
        let maxChipsInRow = 0
        const topicsToCalculate = this.displayTopics
        if (!topicsToCalculate?.length || !this.articles?.length) {
          rowHeights[rowKey] = `${baselineMinHeight}px`
          return
        }

        topicsToCalculate.forEach(topic => {
          let count = 0
          if (rowKey === 'product') {
            const productArticles = this.getTopicProductArticles(topic)
            count = Array.isArray(productArticles) ? productArticles.length : 0
          } else {
            const funnelArticles = this.getTopicArticles(topic, rowKey)
            count = Array.isArray(funnelArticles) ? funnelArticles.length : 0
          }
          maxChipsInRow = Math.max(maxChipsInRow, count)
        })

        const calculatedMinHeight = maxChipsInRow * chipHeightEstimate + cellPadding
        rowHeights[rowKey] = `${Math.max(calculatedMinHeight, baselineMinHeight)}px`
      })
      this.rowMinHeights = rowHeights
    },
    getRowStyle (rowKey) {
      return {
        minHeight: `min(${this.rowMinHeights[rowKey] || '100px'}, 25vh)`,
        maxHeight: '25vh',
        padding: '8px',
        overflowY: 'auto',
        borderBottom: '1px solid rgba(0,0,0,0.12)',
        borderRight: '1px solid rgba(0,0,0,0.12)'
      }
    },
    getLabelStyle (rowKey) {
      const calculatedMinHeightPx = this.rowMinHeights[rowKey] || '100px'
      return {
        minHeight: `min(${calculatedMinHeightPx}, 25vh)`,
        maxHeight: '25vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderBottom: '1px solid rgba(0,0,0,0.12)',
        borderRight: '1px solid rgba(0,0,0,0.12)'
      }
    },
    calculateColumnWidths () {
      const widths = {}
      if (!this.topics?.length || !this.articles?.length) {
        this.columnWidths = {}
        return
      }

      const topicsToCalculate = this.displayTopics
      const avgCharWidth = 6
      const chipPadding = 24
      const columnPadding = 16

      topicsToCalculate.forEach(topic => {
        let maxChars = 0

        this.articles.forEach(article => {
          const text = article.title === '-' ? article.article_url : article.title
          if (!text) return

          if (topic.id === 'no-hub') {
            if (article.topic_id === null || article.topic_id === undefined) {
              maxChars = Math.max(maxChars, text.length)
            }
          } else {
            if (article.topic_id === topic.id) {
              maxChars = Math.max(maxChars, text.length)
            }
          }
        })

        if (topic.name) {
          maxChars = Math.max(maxChars, topic.name.length)
        }

        const calculatedWidth = maxChars * avgCharWidth + chipPadding + columnPadding
        widths[topic.id] = `${calculatedWidth}px`
      })

      this.columnWidths = widths
    },
    getColumnStyle (topicId) {
      const calculatedWidth = this.columnWidths[topicId] || '200px'
      return {
        width: `clamp(200px, ${calculatedWidth}, 350px)`
      }
    }
  },
  async mounted () {
    await this.loadData()
  }
}
